import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material';

import { Header } from './components/Header';
import { CartDrawer } from './components/CartDrawer';
import { LandingPage } from './pages/LandingPage';
import { MenuPage } from './pages/MenuPage';
import { Checkout } from './pages/Checkout';
import { OrderTracking } from './pages/OrderTracking';
import { AuthPage } from './pages/AuthPage';
import { CompleteProfilePage } from './pages/CompleteProfilePage';
import { AccountPage } from './pages/AccountPage';
import { AuthProvider, useAuth } from './context/AuthContext';

/** Protect a route — redirect to /auth if the user isn't signed in. */
const RequireAuth: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#00C896' }} />
      </Box>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }
  return children;
};

const App: React.FC = () => {
  const [cartOpen, setCartOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // On desktop /menu we use a full-bleed 3-col layout with its own sidebar +
  // language toggle, so hide the global Header to avoid duplication.
  const hideGlobalHeader = isDesktop && location.pathname === '/menu';

  return (
    <AuthProvider>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {!hideGlobalHeader && <Header onOpenCart={() => setCartOpen(true)} />}
        <Box component="main" sx={{ flex: 1, pt: hideGlobalHeader ? 0 : { xs: 7, sm: 8 } }}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/complete-profile" element={<RequireAuth><CompleteProfilePage /></RequireAuth>} />
            <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order/:id" element={<OrderTracking />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Box>
        <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      </Box>
    </AuthProvider>
  );
};

export default App;
